import './PreviewPromptPage.scss';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import Loader from '../../../shared/components/common/loader/Loader';
import Title from '../../../shared/components/title/Title';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../../shared/components/common/mobile-menu/MobilePageHeader';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';
import useFiltersInitializing from '../../../shared/hooks/filters/UseFiltersInitializing';
import {
  getMonitorTypeByChatOptionsType,
  getPeriod,
  prepareAndGetChatType,
} from '../../../shared/helpers/StatsHelpers';
import { QUESTIONS } from '../../../shared/routes/path';
import Stats from './components/stats/Stats';
import { PromptResponse } from '../../../shared/models/monitor/PromptResponse';
import { getKeywordsData } from '../../../shared/services/monitor/keywords/KeywordsService';
import { getPromptData } from '../../../shared/services/monitor/prompt/PromptService';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { useGetProject } from '../../../shared/api/hooks/project/useGetProject';
import { EffectiveFeaturesProvider } from '../../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { MonitorEventLogResponse } from '../../../shared/models/monitor/MonitorEventLogResponse';
import { getEvents } from '../../../shared/services/monitor/events/EventsService';
import { KeywordResponse } from '../../../shared/models/monitor/KeywordResponse';
import { getTags } from '../../../shared/api/services/tags/TagsService';
import { TagsResponse } from '../../../shared/models/tags/TagsResponse';
import FilterPanel from '../../../shared/components/filter-panel/FilterPanel';
import {
  MonitorDataParams,
  useGetMonitorData,
} from '../../../shared/api/hooks/monitor/data/UseGetMonitorData';
import { expandSelectedChatTypesToMonitorTypes } from '../../../shared/helpers/MonitorTypeHelpers';
import {
  getKeywordsDailyStats,
  getPromptBrandsStats,
} from '../../../shared/services/monitor/keywords/stats/KeywordsStatsService';
import { KeywordsDailyStatsResponse } from '../../../shared/models/monitor/keywords/stats/KeywordsDailyStatsResponse';
import { PromptBrandsStatsResponse } from '../../../shared/models/monitor/keywords/stats/PromptBrandsStatsResponse';
import { useGetPromptFanout } from '../../../shared/api/hooks/monitor/features/useGetPromptFanout';
import { useGetFeaturesSummary } from '../../../shared/api/hooks/monitor/features/useGetFeaturesSummary';
import { useGetLatestProducts } from '../../../shared/api/hooks/monitor/features/useGetLatestProducts';
import { getSourcesForMonitor } from '../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { SourceDataResponse } from '../../../shared/models/monitor/data/sources/SourceDataResponse';

/**
 * Preview of a single prompt — the prompt view at /projects/:projectId/questions/:id/:period/:chat.
 * Overview mirrors the project stats scoped to one prompt via promptIds; fanout/ads/shopping come
 * from the features endpoints.
 */
const PreviewPromptPage: React.FC = () => {
  const { projectId, id, period, chat } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { accountResponse } = useAccount();

  const monitorIdNumber = Number(id);
  const projectIdNumber = Number(projectId);

  // Feature-gating for the shared FilterPanel (via useEffectiveFeatures): a superadmin viewing a
  // client's prompt must see the OWNER's models — otherwise the dropdown mixes the superadmin's models
  // (unlocked) with the owner's selection, leaving e.g. GEMINI checked-but-locked. Matches the project
  // page's ProjectsPreviewPageSwitch, so FilterPanel and FiltersContext read the same features.
  const { project } = useGetProject(projectIdNumber);
  const effectiveFeatures = project?.ownerFeatures ?? accountResponse?.features ?? [];
  const effectiveAddons = project?.ownerAddons ?? accountResponse?.addons ?? [];

  const {
    monitorPeriod,
    setMonitorPeriod,
    range,
    selectedChatType,
    setSelectedChatType,
    selectedChatTypes,
  } = useFilters();
  const [searchParams] = useSearchParams();

  const hasExplicitModelFilter = searchParams.get('models') !== null;

  // Model filter as MonitorType (md.type) values — sources / AI-answers / features / the per-prompt
  // monitor_keyword_stats stats all filter on md.type.
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    return expandSelectedChatTypesToMonitorTypes(selectedChatTypes);
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const [promptData, setPromptData] = useState<PromptResponse>();
  const [keywordResponse, setKeywordResponse] = useState<KeywordResponse[]>([]);
  const [tags, setTags] = useState<TagsResponse[]>([]);
  // Z addonem `KEYWORDS_PAGE` tagi nie grają (liczymy po scalonych markach) — strona ich nie dostaje.
  const brandTags = effectiveAddons.includes('KEYWORDS_PAGE') ? [] : tags;
  const [eventsData, setEventsData] = useState<MonitorEventLogResponse[] | null>(null);
  const [sourcesData, setSourcesData] = useState<SourceDataResponse | null>(null);
  const [keywordsDailyStats, setKeywordsDailyStats] = useState<KeywordsDailyStatsResponse | null>(null);
  // Same series, always bucketed per DAY — for the Explore AI Answers table, which does not follow the
  // page period. The endpoint computes both variants in the one request.
  const [keywordsDailyStatsPerDay, setKeywordsDailyStatsPerDay] =
    useState<KeywordsDailyStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [monitorDataPage, setMonitorDataPage] = useState(1);
  const [monitorDataPageSize, setMonitorDataPageSize] = useState(10);

  useEffect(() => {
    const newMonitorPeriod = getPeriod(period);
    if (newMonitorPeriod !== monitorPeriod) {
      setMonitorPeriod(newMonitorPeriod);
    }

    // With ?models= present, FiltersContext derives selectedChatType from the filter (single model →
    // that model, subset → 'ALL'). Overriding it here from the path (always '/all' here) would force
    // 'ALL' and ping-pong the queries — only sync from the path when there is no explicit filter.
    if (!hasExplicitModelFilter) {
      const newChatType = prepareAndGetChatType(accountResponse, 'ALL', chat);
      if (newChatType !== selectedChatType) {
        setSelectedChatType(newChatType);
      }
    }
  }, [period, chat, monitorPeriod, selectedChatType, hasExplicitModelFilter]);

  useEffect(() => {
    if (!id || isNaN(monitorIdNumber) || isNaN(projectIdNumber)) {
      navigate(QUESTIONS);
      return;
    }

    setLoading(true);
    Promise.all([getPromptData(id), getKeywordsData(id), getTags(projectIdNumber)])
      .then(([promptResponse, keywordsResponse, tagsResponse]) => {
        if (!promptResponse || promptResponse.status === 'PENDING') {
          navigate(QUESTIONS);
          return;
        }
        setPromptData(promptResponse);
        setKeywordResponse(keywordsResponse);
        setTags(tagsResponse);
        setLoading(false);
      })
      .catch(() => navigate(QUESTIONS));
  }, [id]);

  useEffect(() => {
    if (!range?.[0] || !range?.[1] || !monitorPeriod || !selectedChatType) {
      return;
    }
    getEvents(projectIdNumber, range[0], range[1], monitorPeriod, selectedChatType).then(setEventsData);
  }, [projectIdNumber, range, monitorPeriod, selectedChatType]);

  useEffect(() => {
    if (!promptData?.keyword || !range?.[0] || !range?.[1] || !selectedChatType) {
      return;
    }
    getSourcesForMonitor(
      String(monitorIdNumber),
      promptData.keyword,
      getMonitorTypeByChatOptionsType(selectedChatType),
      range[0],
      range[1],
      filterMonitorTypes
    ).then(setSourcesData);
  }, [promptData?.keyword, monitorIdNumber, range, selectedChatType, filterMonitorTypes]);

  // Average Position (tile + chart) is sourced LIVE from monitor_keyword_stats via this per-prompt
  // endpoint — same basis as the Explore AI Answers table — not the project_daily_stats aggregate.
  useEffect(() => {
    if (!promptData?.keyword || !range?.[0] || !range?.[1] || !monitorPeriod || !selectedChatType) {
      return;
    }
    getKeywordsDailyStats(
      monitorIdNumber,
      getMonitorTypeByChatOptionsType(selectedChatType),
      monitorPeriod,
      range[0],
      range[1],
      filterMonitorTypes
    ).then((res) => {
      setKeywordsDailyStats(res.keywordsDailyStatsData);
      setKeywordsDailyStatsPerDay(res.keywordsDailyStatsWithoutAggregateData);
    });
  }, [promptData?.keyword, monitorIdNumber, range, monitorPeriod, selectedChatType, filterMonitorTypes]);

  const allowedMonitorTypes = useMemo(
    () => (filterMonitorTypes ? new Set(filterMonitorTypes) : null),
    [filterMonitorTypes]
  );

  const from = range?.[0] ?? null;
  const to = range?.[1] ?? null;

  // Ranking, Visibility Overview and SoV-by-model — all LIVE from monitor_keyword_stats + monitor_data
  // for THIS prompt (never project_daily_*). One call replaces the old project-stats hooks.
  const [promptBrands, setPromptBrands] = useState<PromptBrandsStatsResponse | null>(null);
  const [isPromptBrandsLoading, setIsPromptBrandsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!promptData?.keyword || !range?.[0] || !range?.[1] || !monitorPeriod || !selectedChatType) {
      return;
    }
    setIsPromptBrandsLoading(true);
    getPromptBrandsStats(monitorIdNumber, monitorPeriod, range[0], range[1], filterMonitorTypes)
      .then(setPromptBrands)
      .finally(() => setIsPromptBrandsLoading(false));
  }, [promptData?.keyword, monitorIdNumber, range, monitorPeriod, selectedChatType, filterMonitorTypes]);

  const { fanout, isLoading: isFanoutLoading } = useGetPromptFanout(
    promptData
      ? { monitorId: monitorIdNumber, from, to, models: filterMonitorTypes }
      : null
  );

  const { featuresSummary } = useGetFeaturesSummary(
    promptData
      ? {
          projectId: projectIdNumber,
          from,
          to,
          models: filterMonitorTypes,
          promptIds: [monitorIdNumber],
        }
      : null
  );

  const { latestProducts } = useGetLatestProducts(
    promptData ? { monitorId: monitorIdNumber, from, to, models: filterMonitorTypes } : null
  );

  const monitorDataParams: MonitorDataParams | null = useMemo(() => {
    if (!promptData?.keyword || !from || !to || !monitorPeriod || !selectedChatType) {
      return null;
    }

    return {
      monitorId: monitorIdNumber,
      keyword: promptData.keyword,
      // Explore AI Answers lists INDIVIDUAL answers, so its bucket is always a day, whatever period the
      // page is on. WEEKLY/MONTHLY would fold several answers into one row and desync it from the
      // per-day columns (that is how a Friday mention surfaced on the week's Monday row).
      monitorPeriod: 'DAILY',
      chatType: getMonitorTypeByChatOptionsType(selectedChatType),
      from,
      to,
      page: monitorDataPage,
      pageSize: monitorDataPageSize,
      models: filterMonitorTypes,
      withAggregates: true,
    };
  }, [
    promptData?.keyword,
    from,
    to,
    monitorPeriod,
    selectedChatType,
    monitorIdNumber,
    monitorDataPage,
    monitorDataPageSize,
    filterMonitorTypes,
  ]);

  const { monitorData, isLoading: isMonitorDataLoading } = useGetMonitorData(monitorDataParams);

  const filtersInitializing = useFiltersInitializing();

  if (loading || !promptData) {
    return (
      <div className="page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  const suffix = promptData?.demoVersion ? ' (Demo)' : '';
  const promptName = promptData?.prompt + suffix;

  return (
    <EffectiveFeaturesProvider features={effectiveFeatures} addons={effectiveAddons}>
      <div className="preview-prompt-page page-content">
      <div className="page-box">
        {/* Telefon: nazwa pytania (z flagą kraju) w górnym pasku, filtry pod nim (jak na Źródłach). */}
        <MobilePageHeader
          title={
            <>
              <span
                className={`ico--md ${
                  promptData?.localization ? `fi-${promptData.localization.toLowerCase()}` : 'ico--globe'
                }`}
              />
              <span>{promptName}</span>
            </>
          }
        />
        {isMobile ? (
          <div className="mobile-page-filters">
            <FilterPanel baseUrl={location.pathname} />
          </div>
        ) : (
        <div className="page-box__filters" data-observe-sticky>
          <Title
            isBaseCategory={false}
            title={promptName}
            titleStyle={`ico--md  ${
              promptData?.localization ? `fi-${promptData.localization.toLowerCase()}` : 'ico--globe'
            } `}
            showStatusDot={true}
            children={
              <div className="filters">
                <FilterPanel baseUrl={location.pathname} />
              </div>
            }
          />
        </div>
        )}

        <div className="page-box__stats">
          <Stats
            promptData={promptData}
            projectDefaultKeyword={project?.defaultKeyword}
            keywordResponse={keywordResponse}
            tags={brandTags}
            monitorEventsData={eventsData}
            promptBrands={promptBrands}
            isBrandsStatsLoading={isPromptBrandsLoading || filtersInitializing}
            keywordsDailyStats={keywordsDailyStats}
            keywordsDailyStatsPerDay={keywordsDailyStatsPerDay}
            fanout={fanout}
            isFanoutLoading={isFanoutLoading || filtersInitializing}
            featuresSummary={featuresSummary}
            sourcesData={sourcesData}
            latestProducts={latestProducts}
            allowedMonitorTypes={allowedMonitorTypes}
            filterMonitorTypes={filterMonitorTypes}
            monitorData={monitorData}
            isMonitorDataLoading={isMonitorDataLoading || !monitorDataParams}
            totalAnswers={monitorData?.totalAnswers ?? 0}
            onPageChange={(page, pageSize) => {
              setMonitorDataPage(page);
              setMonitorDataPageSize(pageSize);
            }}
          />
        </div>
      </div>
      </div>
    </EffectiveFeaturesProvider>
  );
};

export default PreviewPromptPage;
